import React, { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useMailStore } from '../../stores/mailStore';
import { useAuthStore } from '../../stores/authStore';
import { isDemoUser } from '../../utils/demoMode';

/**
 * MailPushListener — invisible, always-mounted component that keeps a single
 * lightweight Socket.IO connection alive for the duration of the webmail
 * session. This is separate from TeamChatPage's socket (which only connects
 * when the user opens Team Chat) so that the backend's MailIdleService can
 * start an IMAP IDLE watch for every logged-in user regardless of whether
 * they ever open chat. On 'newMailArrived' it triggers an immediate silent
 * refresh instead of waiting for the 60s fallback poll.
 */
const MailPushListener: React.FC = () => {
  const socketRef = useRef<Socket | null>(null);
  const { user } = useAuthStore();
  const isDemo = isDemoUser(user?.email);

  useEffect(() => {
    if (isDemo) return;
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    if (!token) return;

    const socket = io(window.location.origin, {
      path: '/socket.io',
      auth: { token },
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    socket.on('newMailArrived', () => {
      const { currentFolder, fetchEmailsSilent, fetchFolders } = useMailStore.getState();
      fetchEmailsSilent(currentFolder);
      fetchFolders();
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [isDemo, user?.id]);

  return null;
};

export default MailPushListener;
